import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, CreditCard } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import GiftCardPicker from "../components/GiftCardPicker";
import SpendTierGiftCardCelebration from "../components/SpendTierGiftCardCelebration";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const amount = searchParams.get("amount");
  const method = searchParams.get("method");
  const [paymentStatus, setPaymentStatus] = useState(sessionId ? "checking" : "sumup");
  const [orderDetails, setOrderDetails] = useState(null);
  const { fetchCart } = useCart();
  const { user } = useAuth();

  const SUMUP_LINK = "https://pay.sumup.com/b2c/Q0MLYMPQ";

  useEffect(() => {
    if (!sessionId || !user) return;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;

        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          setOrderDetails(prev => ({ ...prev, order_id: data.order_id }));
          await fetchCart();
          return;
        }
        if (data.status === "expired") {
          setPaymentStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus("timeout");
        }
      } catch {
        setPaymentStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user]);

  useEffect(() => {
    if (!orderId || !user) return;
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        setOrderDetails(res.data);
        await fetchCart();
      } catch (err) {
        console.error("Failed to fetch order:", err);
      }
    };
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, user]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Please log in to view your order.</p>
        <Link to="/login" className="text-[#00a99d] hover:underline">Sign In</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 space-y-6">
      {orderDetails?.gift_card_eligible && (paymentStatus === "paid" || paymentStatus === "sumup") && (
        orderDetails.gift_card_promo_type === "spend300" ? (
          <SpendTierGiftCardCelebration
            value={orderDetails.gift_card_value || 25}
            orderId={orderDetails.id || orderId}
            customerEmail={orderDetails.customer_email || user?.email || ""}
          />
        ) : (
          <GiftCardPicker
            orderId={orderDetails.id || orderId}
            initialChoice={orderDetails.gift_card_choice}
            onSaved={(choice) => setOrderDetails((prev) => ({ ...prev, gift_card_choice: choice }))}
          />
        )
      )}

      <div className="card p-8 text-center" data-testid="payment-success-card">
        {/* Stripe Paid */}
        {paymentStatus === "paid" && (
          <>
            <CheckCircle className="w-16 h-16 text-[#00a99d] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-2">Your order has been confirmed and is being processed.</p>
            <p className="text-sm text-gray-500">A confirmation email has been sent to your email address.</p>
          </>
        )}

        {/* Stripe Checking */}
        {paymentStatus === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#00a99d] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment...</h1>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {/* Stripe Expired/Error */}
        {(paymentStatus === "expired" || paymentStatus === "error" || paymentStatus === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Issue</h1>
            <p className="text-gray-600 mb-4">
              {paymentStatus === "expired" ? "Your payment session expired." : "We couldn't verify your payment. If you were charged, please contact us."}
            </p>
            <Link to="/cart" className="btn-primary inline-block">Return to Cart</Link>
          </>
        )}

        {/* SumUp flow */}
        {paymentStatus === "sumup" && (
          <>
            <CheckCircle className="w-16 h-16 text-[#00a99d] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Order Placed Successfully!</h1>
            <p className="text-gray-600 mb-2">Order #{orderId?.slice(0, 8)}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-6 mb-6" data-testid="payment-prompt">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Complete Your Payment</h3>
              <p className="text-amber-700 mb-1">
                Please pay <strong>&pound;{amount}</strong> via our secure SumUp payment page to confirm your order.
              </p>
              <p className="text-sm text-amber-600 mb-4">Enter the exact amount shown above when prompted.</p>
              <a
                href={SUMUP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-3"
                data-testid="pay-now-btn"
              >
                <CreditCard className="w-5 h-5" />
                Pay &pound;{amount} Now
              </a>
            </div>
          </>
        )}

        {/* Order details shown for paid/sumup */}
        {orderDetails && (paymentStatus === "paid" || paymentStatus === "sumup") && (
          <div className="text-left border-t pt-4 mt-4">
            <h4 className="font-semibold mb-2">Order Details</h4>
            <div className="space-y-2 text-sm">
              {orderDetails.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{item.name} x {item.quantity}</span>
                  <span>&pound;{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-2 font-semibold flex justify-between">
                <span>Total</span>
                <span>&pound;{orderDetails.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <div className="flex gap-4 justify-center">
            <Link to="/account" className="text-[#00a99d] hover:underline">View My Orders</Link>
            <Link to="/products" className="text-[#00a99d] hover:underline">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
